import { roleAtLeast, type UserRole } from '@atlas/types'
import { Button } from '@atlas/ui-primitives'
import type { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { LoadingScreen } from '@/components/LoadingScreen'
import { useAuth } from '@/hooks/useAuth'
import { saveIntendedRoute } from '@/lib/intended-route'

interface ProtectedRouteProps {
	children: ReactNode
	/**
	 * Optional minimum Atlas role (from `GET /users/auth/me`) required to render this route.
	 * A UX optimization ONLY — backend authorization remains authoritative. Omit for an
	 * ordinary authenticated route.
	 */
	minRole?: UserRole
}

/** Safe forbidden state for a frontend role mismatch. Renders no session side effects. */
function ForbiddenScreen() {
	return (
		<div className="min-h-screen flex items-center justify-center bg-ds-surface">
			<div className="max-w-md w-full text-center space-y-3 p-8">
				<h1 className="text-2xl font-bold text-ds-text">Not available</h1>
				<p className="text-sm text-ds-text-muted">You don't have access to this page.</p>
			</div>
		</div>
	)
}

/**
 * Retryable degraded state for a valid OIDC session whose `/users/auth/me` failed on a
 * network error or 5xx. Renders in place (no navigation) so the current route is
 * preserved for a successful retry; does not touch the OIDC session.
 */
function BackendUnavailableScreen({ onRetry }: { onRetry: () => void }) {
	return (
		<div className="min-h-screen flex items-center justify-center bg-ds-surface">
			<div className="max-w-md w-full text-center space-y-4 p-8">
				<h1 className="text-2xl font-bold text-ds-text">Can't reach Atlas</h1>
				<p className="text-sm text-ds-text-muted">
					You're signed in, but we couldn't load your account. Check your connection and try again.
				</p>
				<Button variant="outline" onPress={onRetry}>
					Try again
				</Button>
			</div>
		</div>
	)
}

/**
 * Authentication (ADR-0011) + (optional) role gate (ADR-0012). When unauthenticated
 * it saves the sanitized intended route before redirecting to `/login`. Role gating is
 * a UX optimization only, evaluated against the Atlas role from `GET /users/auth/me` using the
 * shared `roleAtLeast` helper — it fails closed for an absent role, and a mismatch shows
 * a safe forbidden screen WITHOUT clearing OIDC state, renewing a token, or starting a
 * Cognito login. Backend `403 ACCESS_FORBIDDEN` remains the authoritative control.
 */
export function ProtectedRoute({ children, minRole }: ProtectedRouteProps) {
	const { isAuthenticated, isLoading, user, status, retryAuthMe } = useAuth()
	const location = useLocation()

	if (isLoading) {
		return <LoadingScreen />
	}

	// Valid OIDC session, but `/users/auth/me` failed on a network error or 5xx: render a
	// retryable degraded state in place — never redirect to `/login`, clear the OIDC
	// session, or render protected content without a resolved Atlas user.
	if (status === 'backend-unavailable') {
		return <BackendUnavailableScreen onRetry={retryAuthMe} />
	}

	if (!isAuthenticated) {
		saveIntendedRoute(`${location.pathname}${location.search}`)
		return <Navigate to="/login" replace />
	}

	if (minRole && !roleAtLeast(user?.role ?? '', minRole)) {
		return <ForbiddenScreen />
	}

	return <>{children}</>
}
